import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { api, apiClient } from '../api/client';
import { daysUntilCalendarDate } from '../lib/calendar-date';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import AgentMark from '../components/AgentMark';
import { Tooltip } from '../components/ui/tooltip';
import SyncStatusBar from '../components/SyncStatusBar';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { chaseableCount, type EquipmentAlertRow } from '../lib/equipmentAlerts';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import {
  Bot,
  Home,
  LayoutDashboard,
  ClipboardList,
  Truck,
  Ticket,
  Shield,
  Building2,
  Wrench,
  Users,
  GraduationCap,
  DollarSign,
  FileText,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Pin,
  Warehouse,
  CalendarDays,
  Calendar,
  Receipt,
  ReceiptText,
  Bell,
  LogOut,
  ClipboardCheck,
  Clock,
  BarChart2,
  Package,
  MapPin,
  Phone,
  Layers,
  ScrollText,
  Hammer,
  Handshake,
  TrendingUp,
  Plus,
} from 'lucide-react';
import { NEW_LOAD_PATH, NEW_RENTAL_ORDER_PATH } from '../components/overview/OverviewBand';
import { buildCasingSidebarGroups, buildCasingSidebarStructure } from '../config/casingNav';
import { isSalesRole, navPathOpenForRole } from '../lib/sales-access';
import { buildVerticalSidebarStructure, isSidebarItemActive, usesVerticalSidebar, VERTICAL_SIDEBAR_STRUCTURE } from '../config/verticalNav';
import { useDownholeFeature } from '../hooks/useDownholeSettings';
import { useAiAgentProfile } from '../hooks/useAiAgentProfile';

/* ────────────── sidebar nav config ────────────── */

type SidebarItem = {
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
};

type SidebarGroup = {
  key: string;
  label: string;
  labelColor?: string;
  items: SidebarItem[];
  /**
   * Set only by the trucking / rentals structure (verticalNav.ts). A `section` is an always-
   * open heading; a `row` is a nav-sized row with a chevron at the foot of the list. Absent on
   * the casing and legacy groups, which render exactly as they did before `kind` existed.
   */
  kind?: 'section' | 'row';
  rowIcon?: React.ComponentType<{ size?: number; className?: string }>;
};

// Maps lucide icon names (strings from casingNav.ts) to their components.
const SIDEBAR_ICON: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Home,
  ClipboardList,
  Truck,
  Ticket,
  Shield,
  Phone,
  Building2,
  Wrench,
  Users,
  CalendarDays,
  Clock,
  GraduationCap,
  BarChart3,
  DollarSign,
  ClipboardCheck,
  FileText,
  Receipt,
  ReceiptText,
  Bot,
  Bell,
  Settings,
  LayoutDashboard,
  Calendar,
  Package,
  MapPin,
  Layers,
  ScrollText,
  Hammer,
  Handshake,
  TrendingUp,
};

const SIDEBAR_PINNED_KEY = 'sidebar:pinned';

/* The casing structure namespaces its section keys. A casing tenant's OPERATIONS group and
 * a trucking-only tenant's OPERATIONS group are different lists with different defaults, and
 * an unprefixed key would have them share one stored collapsed state. */
const casingSectionKey = (section: string) => `casing:${section.toLowerCase()}`;
/* Same reason for the trucking / rentals structure: its OPERATIONS is a third list. */
const verticalSectionKey = (section: string) => `vertical:${section.toLowerCase().replace(/\s+/g, '-')}`;
const sidebarSectionKey = (verticalKey: string) =>
  `sidebar:section:${verticalKey}:collapsed`;

/**
 * Per-section collapsed state, scoped to the browsing SESSION.
 *
 * sessionStorage rather than localStorage: the brief asks for the open/closed state to be
 * remembered per user within the session only, with no new relation and no API. sessionStorage
 * is exactly that scope — it survives route changes and remounts in this tab and is gone on
 * the next one, so a changed default (FINANCE and SYSTEM now start collapsed) is not
 * outvoted forever by a toggle someone made months ago.
 *
 * Every accessor is wrapped: in a private window, with site data blocked, and during
 * thumbnail capture the read can come back empty or the property access can throw.
 * `defaults` therefore has to produce a correct sidebar on its own.
 */
function useSectionCollapse(defaults: Record<string, boolean>) {
  const defaultsRef = useRef(defaults);
  const [state, setState] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = { ...defaults };
    try {
      for (const k of Object.keys(init)) {
        const raw = window.sessionStorage.getItem(sidebarSectionKey(k));
        if (raw === 'true') init[k] = true;
        else if (raw === 'false') init[k] = false;
      }
    } catch {
      // sessionStorage unavailable — defaults apply
    }
    return init;
  });

  const toggle = React.useCallback((key: string) => {
    setState((prev) => {
      const current = prev[key] ?? defaultsRef.current[key] ?? false;
      const next = { ...prev, [key]: !current };
      try {
        window.sessionStorage.setItem(sidebarSectionKey(key), String(next[key]));
      } catch {
        // ignore; in-memory only
      }
      return next;
    });
  }, []);

  return [state, toggle] as const;
}

export function DesktopSidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, hasVertical, user } = useAuth();
  const sidebarZone = useTenantTimeZone();
  const [pinnedPaths, setPinnedPaths] = useLocalStorage<string[]>(SIDEBAR_PINNED_KEY, []);

  // A sales user is refused the agent, company settings, equipment alerts, cert
  // summary and user list (api#1032), and every nav entry those feed is hidden
  // from them — so none of these reads is fired for that role.
  const isSales = isSalesRole(user?.role);
  const { data: agentProfile } = useAiAgentProfile({ enabled: !isSales });
  const agentAvatarUrl = agentProfile?.avatar_url || '';
  // The avatar must degrade to the AgentMark, never to nothing. `avatar_url` is not always a
  // URL — Smith's row holds raw SVG markup, which resolves against the origin and 404s — and
  // hiding the broken image on error leaves an entry with no icon at all. Collapsed, that is
  // an empty pill.
  const [agentAvatarFailed, setAgentAvatarFailed] = useState(false);
  const showAgentAvatar = Boolean(agentAvatarUrl) && !agentAvatarFailed;

  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    enabled: !isSales,
    queryFn: () => apiClient.get('/settings/company').then(r => r.data?.data ?? r.data ?? {}),
    staleTime: 60_000,
  });
  const truckingToggles = companySettings?.trucking ?? {};

  const { data: truckingFeatures } = useQuery({
    queryKey: ['settings', 'trucking'],
    queryFn: () => apiClient.get('/settings/trucking').then(r => r.data?.data ?? r.data ?? {}),
    staleTime: 60_000,
    enabled: hasVertical('trucking'),
  });
  const ddMwdEnabled = useDownholeFeature('dd_mwd');

  // Badge: alerts total (gear out with no disposition + cert usage ≥75% +
  // crew cert/DL expiring ≤90d)
  //
  // SAME SOURCE AS THE SCREEN, deliberately. This badge and AlertsScreen answer
  // one question and used to answer it from two places — this counted
  // `status === 'left_on_site'` off the whole equipment table while the screen
  // did its own filtering. The moment the screen started showing the
  // `finished_job` class too, a badge computed the old way would have read 4
  // against a screen showing 47. One endpoint, one helper, shared query key.
  const { data: equipmentAlertsData } = useQuery({
    queryKey: ['equipment-alerts'],
    enabled: !isSales,
    queryFn: () =>
      api.dispatch.equipmentAlerts().then(r => (r.data?.data ?? []) as EquipmentAlertRow[]),
    staleTime: 60_000,
  });
  const { data: certSummaryData } = useQuery({
    queryKey: ['cert-summary-home'],
    enabled: !isSales,
    queryFn: () => api.equipment.certSummary().then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    enabled: !isSales,
    queryFn: () => api.users.list().then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });

  const alertsTotal = useMemo(() => {
    const gearOut = chaseableCount(equipmentAlertsData);

    const certs: any[] = certSummaryData ?? [];
    const certAlerts = certs.filter((e: any) => (e.percentUsed ?? 0) >= 75).length;

    const allUsers: any[] = usersData ?? [];
    let crewCount = 0;
    for (const member of allUsers) {
      for (const cert of (member.certifications || [])) {
        const daysLeft = daysUntilCalendarDate(cert.expiryDate, sidebarZone);
        if (daysLeft != null && daysLeft <= 90) crewCount++;
      }
      const dlExpiry = member.driversLicenceExpiry || member.drivers_licence_expiry;
      const dlDaysLeft = daysUntilCalendarDate(dlExpiry, sidebarZone);
      if (dlDaysLeft != null && dlDaysLeft <= 90) crewCount++;
    }

    return gearOut + certAlerts + crewCount;
  }, [equipmentAlertsData, certSummaryData, usersData, sidebarZone]);

  // The trucking Intake badge is gone (#815). The Intake page was deleted in
  // #836, so the badge had no item to sit on, and it polled /trucking/intake
  // every 30s for a `pending` status that table cannot hold.

  // Map nav paths to badge counts
  const badgeCounts: Record<string, number> = {};
  if (alertsTotal > 0) badgeCounts['/alerts'] = alertsTotal;

  const truckingItems = [
    // No Dashboard: /trucking is retired and redirects to the Overview, which this
    // sidebar already lists (fix/trucking-nav).
    { path: '/trucking/dispatch', icon: Truck, label: 'Dispatch' },
    { path: '/trucking/calendar', icon: Calendar, label: 'Calendar' },
    ...(truckingFeatures?.bol_enabled !== false ? [{ path: '/trucking/bols', icon: FileText, label: 'Bills of Lading' }] : []),
    ...(truckingFeatures?.weight_tickets_enabled !== false ? [{ path: '/trucking/weight-tickets', icon: ClipboardList, label: 'Weight Tickets' }] : []),
    ...(truckingFeatures?.move_jobs_enabled !== false ? [{ path: '/trucking/move-jobs', icon: ClipboardList, label: 'Move Jobs' }] : []),
    { path: '/trucking/fleet', icon: Wrench, label: 'Fleet' },
    { path: '/trucking/drivers', icon: Users, label: 'Drivers' },
    // The trucking rate card (api#950).
    { path: '/trucking/rates', icon: DollarSign, label: 'Rates' },
    // TruckingReportsScreen was routed with no nav entry anywhere. The only
    // links to it were in ReportsScreen, which is itself routed solely at
    // /safety/reports and linked from nothing.
    { path: '/trucking/reports', icon: BarChart3, label: 'Reports' },
  ];

  const rentalsItems = [
    { path: '/rentals', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/rentals/dispatch', icon: Truck, label: 'Dispatch Board' },
    { path: '/rentals/active', icon: ClipboardList, label: 'Active Rentals' },
    { path: '/rentals/assets', icon: Package, label: 'Rental Equipment' },
    { path: '/rentals/yard', icon: MapPin, label: 'Yard/Check-Out' },
    { path: '/rentals/inspections', icon: ClipboardCheck, label: 'Inspections' },
    { path: '/rentals/damage-charges', icon: ReceiptText, label: 'Damage Charges' },
    // A rentals tenant hauls its own equipment (#815, api#1041).
    { path: '/trucking/fleet', icon: Wrench, label: 'Fleet' },
    { path: '/trucking/drivers', icon: Users, label: 'Drivers' },
    { path: '/rentals/tickets', icon: FileText, label: 'Field Tickets' },
    { path: '/rentals/maintenance', icon: Wrench, label: 'Maintenance' },
    { path: '/rentals/price-books', icon: DollarSign, label: 'Price Books' },
    // /rentals/reports and its two children were routed with no way in.
    // RentalReports links onward to /rentals/reports/customer and
    // /rentals/reports/equipment, so one entry opens all three.
    { path: '/rentals/reports', icon: BarChart3, label: 'Reports' },
  ];

  const rentalsGroup: SidebarGroup = { key: 'rentals', label: 'SURFACE RENTALS', labelColor: 'text-emerald-500', items: rentalsItems };

  // DOWNHOLE TOOLS is not a group here. A downhole tenant gets the grouped
  // vertical sidebar (verticalNav.ts), which lists each rebuilt /downhole screen.

  // Casing sidebar groups sourced from the single-source nav config.
  // CORE is casing-gated; OPERATIONS/REPORTS/FINANCE/SYSTEM are shared across verticals.
  const AgentAvatarIcon = useMemo(() => {
    if (!agentAvatarUrl) return null;
    // eslint-disable-next-line react/display-name
    return ({ size = 20 }: { size?: number; className?: string }) => (
      <img
        src={agentAvatarUrl}
        alt="AI Agent"
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }, [agentAvatarUrl]);

  const isCasing = hasVertical('casing_running');

  /* ── Which structure renders ──
   *
   * The casing sidebar is the grouped FAVORITES/OPERATIONS/REPORTS/FINANCE/SYSTEM list in
   * casingNav.ts. Everyone else keeps the sidebar they have today, built from each nav
   * item's `section` field. The switch is one positive vertical check, `hasVertical
   * ('casing_running')`, which reads `enabled_verticals` off the company record through
   * AuthContext — no schema, no new flag.
   *
   * Non-casing tenants reach this because the legacy shared groups (OPERATIONS, REPORTS,
   * FINANCE, SYSTEM) come from the same registry a casing tenant uses. Re-sectioning items
   * in place would have restructured trucking's and rentals' nav as a side effect, so the
   * casing structure is a separate whitelist rather than an edit to `section`.
   */

  // Legacy structure, still rendered for every non-casing tenant.
  const legacyGroups: SidebarGroup[] = buildCasingSidebarGroups().map((g) => ({
    key: g.section.toLowerCase(),
    label: g.section,
    items: g.items.map((it) => ({
      path: it.path,
      label: it.label,
      icon: it.id === 'ai-agent' && AgentAvatarIcon ? AgentAvatarIcon : SIDEBAR_ICON[it.icon],
    })),
  }));
  const legacyCoreGroup = legacyGroups.find((g) => g.key === 'core');
  const rawSharedGroups = legacyGroups.filter((g) => g.key !== 'core');

  // Safety sits in the legacy CORE group, but the screens behind it — equipment
  // checks, JSAs, incidents, near-miss reports — are cross-vertical. When casing is off,
  // surface it in the shared OPERATIONS group so every tenant keeps the link. BottomNav
  // does the same on mobile, appending a shared Safety tab when casing isn't providing one.
  const safetyItem = legacyCoreGroup?.items.find((i) => i.path === '/safety');
  const sharedGroups: SidebarGroup[] =
    safetyItem
      ? rawSharedGroups.map((g) =>
          g.key === 'operations' ? { ...g, items: [safetyItem, ...g.items] } : g,
        )
      : rawSharedGroups;

  // Casing structure. Safety is a named member of OPERATIONS here, so it needs no injection.
  const casingGroups: SidebarGroup[] = buildCasingSidebarStructure().map((g) => ({
    key: casingSectionKey(g.section),
    label: g.section,
    items: g.items.map((it) => ({
      path: it.path,
      label: it.label,
      icon: SIDEBAR_ICON[it.icon],
    })),
  }));

  // One page, one entry: Fleet and Drivers are in both lists, and the first group to list a
  // path keeps it (a tenant with trucking and rentals sees them under TRUCKING only).
  const listedPaths = new Set<string>();
  const verticalGroups: SidebarGroup[] = [
    ...(hasVertical('trucking') ? [{ key: 'trucking', label: 'TRUCKING', labelColor: 'text-amber-500', items: truckingItems } as SidebarGroup] : []),
    ...(hasVertical('surface_rentals') ? [rentalsGroup] : []),
  ].map((g) => ({
    ...g,
    items: g.items.filter((it) => {
      if (listedPaths.has(it.path)) return false;
      listedPaths.add(it.path);
      return true;
    }),
  }));

  // Trucking / rentals / downhole structure (verticalNav.ts). Renders for a tenant with any of
  // those verticals and no casing; a casing tenant keeps the structure above (it is checked
  // first, below), and a tenant with none of them keeps the legacy sectioned list.
  const hasTruckingOrRentals = usesVerticalSidebar(hasVertical);
  const truckingRentalsGroups: SidebarGroup[] = buildVerticalSidebarStructure(
    hasVertical,
    truckingFeatures,
    { dd_mwd: ddMwdEnabled },
  ).map((g) => ({
    key: verticalSectionKey(g.section),
    label: g.kind === 'row' ? g.rowLabel ?? g.section : g.section,
    kind: g.kind,
    rowIcon: g.rowIcon ? SIDEBAR_ICON[g.rowIcon] : undefined,
    items: g.items.map((it) => ({
      path: it.path,
      label: it.label,
      icon: SIDEBAR_ICON[it.icon],
    })),
  }));

  // The trucking / rentals layout draws its groups differently (always-open sections on top,
  // folded rows at the foot). Casing and legacy keep the collapsible headings below, untouched.
  const usesVerticalLayout = hasTruckingOrRentals;
  const topGroups = truckingRentalsGroups.filter((g) => g.kind !== 'row');
  const rowGroups = truckingRentalsGroups.filter((g) => g.kind === 'row');

  const allSidebarGroups: SidebarGroup[] = isCasing
    ? [casingGroups[0], ...verticalGroups, ...casingGroups.slice(1)]
    : hasTruckingOrRentals
      ? truckingRentalsGroups
      : [...verticalGroups, ...sharedGroups];
  // A sales user sees only entries whose route they may open (lib/sales-access.ts);
  // a group left empty is dropped. Every other role keeps every entry.
  const sidebarGroups: SidebarGroup[] = allSidebarGroups
    .map((g) => ({ ...g, items: g.items.filter((it) => navPathOpenForRole(user?.role, it.path)) }))
    .filter((g) => g.items.length > 0);

  // Collapsed-by-default sections come from the structure list for casing tenants; the
  // legacy sidebar keeps every group open, which is what it does today.
  const sectionDefaults = useMemo(() => {
    const d: Record<string, boolean> = {};
    for (const g of buildCasingSidebarStructure()) {
      d[casingSectionKey(g.section)] = g.defaultCollapsed;
    }
    for (const g of VERTICAL_SIDEBAR_STRUCTURE) {
      d[verticalSectionKey(g.section)] = g.kind === 'row' && g.defaultCollapsed;
    }
    return d;
  }, []);
  const [sectionCollapsed, toggleSection] = useSectionCollapse(sectionDefaults);

  // Flat lookup from path → item across every rendered group, used to resolve pinned items.
  // Rebuilt on each render rather than memoized: the groups array is itself rebuilt every
  // render, so a
  // useMemo over it recomputes every render while claiming it does not.
  const itemByPath = new Map<string, SidebarItem>();
  for (const g of sidebarGroups) {
    for (const it of g.items) itemByPath.set(it.path, it);
  }

  const pinnedItems: SidebarItem[] = pinnedPaths
    .map((p) => itemByPath.get(p))
    .filter((i): i is SidebarItem => Boolean(i));

  const isPinned = (path: string) => pinnedPaths.includes(path);
  const togglePin = (path: string) =>
    setPinnedPaths((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path],
    );

  const renderItem = (item: SidebarItem, keySuffix = '') => {
    const Icon = item.icon;
    const isActive = isSidebarItemActive(item.path, location.pathname);
    const badgeCount = badgeCounts[item.path] ?? 0;
    const pinned = isPinned(item.path);

    return (
      <div
        key={item.path + ':' + item.label + keySuffix}
        className="relative group"
      >
        {/* The label is text when expanded, so the bubble only exists when collapsed. */}
        <Tooltip content={collapsed ? item.label : ''} side="right" className="flex w-full">
        <button
          onClick={() => navigate(item.path)}
          aria-label={item.label}
          className={`w-full flex items-center gap-3 transition-colors rounded-lg ${
            collapsed ? 'justify-center px-0 py-2.5 mx-2 w-12' : 'pl-4 pr-9 py-2'
          } ${
            isActive
              ? 'text-fieldiq-accent bg-fieldiq-accent/10'
              : 'text-fieldiq-text hover:text-white hover:bg-fieldiq-border/50'
          }`}
        >
          <span className="relative flex-shrink-0">
            <Icon size={20} />
            {badgeCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold leading-none px-1 bg-amber-500">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </span>
          {!collapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
        </button>
        </Tooltip>
        {!collapsed && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePin(item.path); }}
            aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
            aria-pressed={pinned}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity ${
              pinned
                ? 'opacity-100 text-fieldiq-accent'
                : 'opacity-0 group-hover:opacity-100 focus:opacity-100 text-fieldiq-muted hover:text-fieldiq-text'
            }`}
          >
            <Pin size={14} className={pinned ? 'rotate-45' : ''} />
          </button>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`hidden md:flex flex-col flex-shrink-0 bg-fieldiq-card border-r border-fieldiq-border transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-52'
      }`}
    >
      {/* The nav list is the ONLY scroller in the sidebar. The zero-minimum-height utility
        * below is what makes that true: a column child otherwise refuses to become shorter
        * than the rows inside it, so the list pushes the footer past the bottom edge and Sign
        * Out rides up into whichever group sits above it — where a red row directly beneath
        * a nav item reads as a nav item. */}
      {usesVerticalLayout ? (
        <div className="flex-1 min-h-0 py-4 overflow-y-auto flex flex-col">
          <div className="space-y-5">
          {/* Making a rental is one click from anywhere (operator, 2026-10-15): the
            * first thing in a rentals tenant's sidebar. Collapsed to the icon rail it
            * keeps its name for a screen reader and a tooltip. */}
          {hasVertical('surface_rentals') && (
            <div className={collapsed ? 'flex justify-center' : 'px-3'}>
              <Tooltip content={collapsed ? 'New Rental Order' : ''} side="right" className={collapsed ? undefined : 'flex w-full'}>
                <button
                  type="button"
                  data-testid="sidebar-new-rental-order"
                  onClick={() => navigate(NEW_RENTAL_ORDER_PATH)}
                  aria-label="New Rental Order"
                  className={`fi-action fi-action--standard fi-action--primary ${collapsed ? '' : 'w-full justify-center'}`}
                >
                  <Plus size={16} aria-hidden="true" />
                  {!collapsed && <span>New Rental Order</span>}
                </button>
              </Tooltip>
            </div>
          )}
          {/* Booking a load is one click from anywhere, as a rental order is
            * (fix/trucking-nav). Its own positive guard: a tenant with both gets both. */}
          {hasVertical('trucking') && (
            <div className={collapsed ? 'flex justify-center' : 'px-3'}>
              <Tooltip content={collapsed ? 'New Load' : ''} side="right" className={collapsed ? undefined : 'flex w-full'}>
                <button
                  type="button"
                  data-testid="sidebar-new-load"
                  onClick={() => navigate(NEW_LOAD_PATH)}
                  aria-label="New Load"
                  className={`fi-action fi-action--standard fi-action--primary ${collapsed ? '' : 'w-full justify-center'}`}
                >
                  <Plus size={16} aria-hidden="true" />
                  {!collapsed && <span>New Load</span>}
                </button>
              </Tooltip>
            </div>
          )}
          {pinnedItems.length > 0 && (
            <div>
              {!collapsed && (
                <p className="px-4 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fieldiq-muted">
                  PINNED
                </p>
              )}
              <div className="space-y-0.5">
                {pinnedItems.map((item) => renderItem(item, ':pinned'))}
              </div>
            </div>
          )}

            {topGroups.map((group) => (
              <div key={group.key}>
                {!collapsed && (
                  <p className={`px-4 mb-1.5 text-[10px] font-bold uppercase tracking-widest ${group.labelColor || 'text-fieldiq-muted'}`}>
                    {group.label}
                  </p>
                )}
                <div id={`sidebar-section-${group.key}`} className="space-y-0.5">
                  {group.items.map((item) => renderItem(item))}
                </div>
              </div>
            ))}
          </div>

          {/* The folded rows. No heading above them; they follow the last section at the
            * ordinary section gap (pt-5 matches the space-y-5 above). They used to be pushed
            * to the foot of the list, which on a short menu — a pressure-testing-only tenant
            * — left a screen-high empty gap between REPORTS and Company (2026-10-08). The
            * Agent card and Sign Out are what stay at the bottom, in the footer below, which
            * is outside this scroller. */}
          {rowGroups.length > 0 && (
            <div className="pt-5 space-y-0.5">
              {rowGroups.map((group) => {
                if (collapsed) {
                  // An icon rail has no room for a row, so every item shows, as before.
                  return <div key={group.key} className="space-y-0.5">{group.items.map((item) => renderItem(item))}</div>;
                }
                const isShut = sectionCollapsed[group.key] ?? true;
                // A shut row must not hide what its badges say: the alerts total rides on
                // the row while it is shut. Open, each item carries its own.
                const hiddenBadgeTotal = isShut
                  ? group.items.reduce((n, it) => n + (badgeCounts[it.path] ?? 0), 0)
                  : 0;
                const RowIcon = group.rowIcon;
                return (
                  <div key={group.key}>
                    <button
                      type="button"
                      onClick={() => toggleSection(group.key)}
                      aria-expanded={!isShut}
                      aria-controls={`sidebar-section-${group.key}`}
                      aria-label={hiddenBadgeTotal > 0 ? `${group.label}, ${hiddenBadgeTotal} waiting` : group.label}
                      className="w-full flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg transition-colors text-fieldiq-text hover:text-white hover:bg-fieldiq-border/50"
                    >
                      {RowIcon && <RowIcon size={20} className="flex-shrink-0" />}
                      <span className="flex-1 text-left text-sm font-medium truncate">{group.label}</span>
                      {hiddenBadgeTotal > 0 && (
                        <span
                          aria-hidden="true"
                          className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-fi-signal-amber text-fi-signal-ink text-[10px] font-bold leading-none px-1"
                        >
                          {hiddenBadgeTotal > 99 ? '99+' : hiddenBadgeTotal}
                        </span>
                      )}
                      <ChevronDown
                        size={16}
                        className={`flex-shrink-0 text-fieldiq-muted transition-transform duration-150 ${isShut ? '-rotate-90' : ''}`}
                      />
                    </button>
                    {!isShut && (
                      <div id={`sidebar-section-${group.key}`} className="space-y-0.5 pl-3">
                        {group.items.map((item) => renderItem(item))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 py-4 space-y-5 overflow-y-auto">
          {pinnedItems.length > 0 && (
            <div>
              {!collapsed && (
                <p className="px-4 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-fieldiq-muted">
                  PINNED
                </p>
              )}
              <div className="space-y-0.5">
                {pinnedItems.map((item) => renderItem(item, ':pinned'))}
              </div>
            </div>
          )}

          {sidebarGroups.map((group) => {
            if (group.items.length === 0) return null;
            const sectionIsCollapsed = sectionCollapsed[group.key] ?? false;
            const showItems = collapsed || !sectionIsCollapsed;
            // A shut group must not hide what its badges say, so a collapsed group's
            // header carries the group's badge total while it is shut. Scoped to the trucking / rentals structure: the casing sidebar
            // is unchanged by this.
            const hiddenBadgeTotal =
              sectionIsCollapsed && group.key.startsWith('vertical:')
                ? group.items.reduce((n, it) => n + (badgeCounts[it.path] ?? 0), 0)
                : 0;
            return (
              <div key={group.key}>
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleSection(group.key)}
                    aria-expanded={!sectionIsCollapsed}
                    aria-controls={`sidebar-section-${group.key}`}
                    className={`w-full px-4 mb-1.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 hover:opacity-80 transition-opacity ${group.labelColor || 'text-fieldiq-muted'}`}
                  >
                    <ChevronDown
                      size={12}
                      className={`flex-shrink-0 transition-transform duration-150 ${sectionIsCollapsed ? '-rotate-90' : ''}`}
                    />
                    <span>{group.label}</span>
                    {hiddenBadgeTotal > 0 && (
                      <span
                        aria-label={`${hiddenBadgeTotal} waiting`}
                        className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-fi-signal-amber text-fi-signal-ink text-[10px] font-bold leading-none px-1 tracking-normal"
                      >
                        {hiddenBadgeTotal > 99 ? '99+' : hiddenBadgeTotal}
                      </span>
                    )}
                  </button>
                )}
                {showItems && (
                  <div id={`sidebar-section-${group.key}`} className="space-y-0.5">
                    {group.items.map((item) => renderItem(item))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pinned footer ──
        * Outside the scroller, and held at their natural height, so these three cannot
        * travel with the nav list. Reading bottom-up: collapse chevron, Sign Out, FieldIQ
        * Agent card — which is this DOM order reversed. */}
      <div className="flex-shrink-0 flex flex-col">
        {/* FieldIQ Agent — replaces the floating action button that used to sit over the
          * bottom-right of every screen. Drawn for sales too (api#1076); their profile
          * read stays off, so the card shows the AgentMark. */}
        <Tooltip content={collapsed ? 'FieldIQ Agent' : ''} side="right" className={`flex ${collapsed ? 'self-center' : ''}`}>
        <button
          onClick={() => navigate('/agent')}
          aria-label="FieldIQ Agent"
          className={`flex items-center transition-colors border border-fieldiq-border rounded-lg hover:border-fieldiq-accent/60 hover:bg-fieldiq-border/40 ${
            collapsed ? 'justify-center p-2 mx-2 mb-2 w-12' : 'flex-1 min-w-0 gap-2 px-2.5 py-2.5 mx-2 mb-2 text-left'
          }`}
        >
          <span className="flex-shrink-0 flex items-center justify-center">
            {showAgentAvatar ? (
              <img
                src={agentAvatarUrl}
                alt=""
                className="rounded-full object-cover"
                style={{ width: 22, height: 22 }}
                onError={() => setAgentAvatarFailed(true)}
              />
            ) : (
              <AgentMark />
            )}
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-fieldiq-text truncate">FieldIQ Agent</span>
                <span className="block text-[11px] text-fieldiq-muted truncate">Search or start a task</span>
              </span>
              <ChevronRight size={16} className="flex-shrink-0 text-fieldiq-muted" />
            </>
          )}
        </button>
        </Tooltip>

        {/* Sign Out */}
        <Tooltip content={collapsed ? 'Sign Out' : ''} side="right" className={`flex ${collapsed ? 'self-center' : ''}`}>
        <button
          onClick={logout}
          aria-label="Sign Out"
          className={`flex items-center gap-3 transition-colors text-red-400 hover:text-red-300 hover:bg-red-500/10 ${
            collapsed ? 'justify-center px-0 py-2.5 mx-2 rounded-lg w-12' : 'flex-1 px-4 py-2 mx-2 rounded-lg'
          }`}
        >
          <LogOut size={20} className="flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
        </button>
        </Tooltip>

        {/* Collapse toggle */}
        <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right" className="flex w-full">
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="w-full border-t border-fieldiq-border p-3 flex items-center justify-center text-fieldiq-muted hover:text-white transition-colors"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        </Tooltip>
      </div>
    </aside>
  );
}

/* ────────────── shared layout ────────────── */

export function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // <main> is this tree's only scroll container, so it is the one Back has to
  // put back where the reader left it.
  const mainRef = useRef<HTMLElement>(null);
  useScrollRestoration(() => mainRef.current);

  // PRINT: the one-screen-high shell and its scrolling <main> are undone, so a
  // long page flows onto as many sheets as it needs instead of being cut off at
  // the first one (#803, the printed BOL). The sync bar and header do not print,
  // and the page colour is dropped so the sheet is the paper, not grey.
  return (
    <div data-testid="app-shell" className="animate-fade-in h-screen bg-fieldiq-dark flex flex-col overflow-hidden print:h-auto print:overflow-visible print:block print:bg-transparent">
      <div data-testid="app-sync-bar" className="print:hidden">
        <SyncStatusBar />
      </div>
      <Header />
      <div data-testid="app-shell-row" className="flex flex-1 min-h-0 print:block">
        <DesktopSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
        <main ref={mainRef} className="flex-1 overflow-y-auto print:overflow-visible">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
